/**
 * Recursively removes all keys with empty values from an object or array.
 * Empty values are considered to be `null`, `undefined`, an empty string `""`, or an empty object `{}`.
 * For arrays, it filters out `undefined` values and applies the same removal logic to each item.
 *
 * @template T - A generic type extending `Record<string, any>` or array.
 * @param {T} obj - The object or array from which to remove empty keys.
 * @returns {T} A new object or array with empty keys removed.
 */
function removeEmptyObjectKeys<T>(obj: T): T {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    // For arrays, filter out undefined values and process each item
    return obj
      .map((item) => removeEmptyObjectKeys(item))
      .filter((item) => item !== undefined) as T;
  }

  // For objects, remove empty keys
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const processedValue = removeEmptyObjectKeys(value);

    // Skip empty values
    if (
      processedValue === null ||
      processedValue === undefined ||
      processedValue === ""
    ) {
      continue;
    }
    // Skip empty objects (but not arrays or other types)
    if (
      typeof processedValue === "object" &&
      !Array.isArray(processedValue) &&
      Object.keys(processedValue).length === 0
    ) {
      continue;
    }

    result[key] = processedValue;
  }

  return result as T;
}

export default removeEmptyObjectKeys;
